// frontend/src/features/inventory/lookup/useLotLookup.ts  (فایل جدید)
// AM-036 فاز ۲ §2.4: هوک واحد استعلام پالت — جایگزین دو منطق اسکن جدا در StockPage.
//  - AbortController: اسکن دوم، درخواست اول را لغو می‌کند.
//  - isScanning: فقط وقتی آخرین درخواست تمام شد false می‌شود.
//  - کد با params ارسال می‌شود (نه الحاق به URL).
//  - خطاها به پیام فارسی قابل‌نمایش تبدیل می‌شوند.

import { useCallback, useEffect, useRef, useState } from "react";
import { axiosInstance } from "@/api/client";
import type { LotLookupResponse, LotLookupResult } from "./lotLookupTypes";
import { extractApiErrorMessage } from "./lotScan";

const LOOKUP_URL = "/api/v1/inventory/lots/lookup/";

interface AxiosLikeError {
  code?: string;
  name?: string;
  response?: { status?: number; data?: unknown };
}

function asAxiosLike(err: unknown): AxiosLikeError {
  return typeof err === "object" && err !== null ? (err as AxiosLikeError) : {};
}

function isCanceled(err: AxiosLikeError): boolean {
  return err.code === "ERR_CANCELED" || err.name === "CanceledError" || err.name === "AbortError";
}

export function useLotLookup() {
  const [isScanning, setIsScanning] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => controllerRef.current?.abort();
  }, []);

  const lookup = useCallback(async (rawCode: string): Promise<LotLookupResult> => {
    const code = rawCode.trim();
    if (!code) return { status: "error", message: "کد پالت خالی است." };

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setIsScanning(true);

    try {
      const res = await axiosInstance.get<LotLookupResponse>(LOOKUP_URL, {
        params: { code },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return { status: "cancelled" };
      return { status: "ok", lot: res.data };
    } catch (raw) {
      const err = asAxiosLike(raw);
      if (controller.signal.aborted || isCanceled(err)) return { status: "cancelled" };
      const httpStatus = err.response?.status;
      if (httpStatus === 404) {
        return { status: "error", httpStatus, message: `پالتی با شماره ${code} یافت نشد.` };
      }
      if (httpStatus === 403) {
        return {
          status: "error",
          httpStatus,
          message: "دسترسی لازم برای استعلام پالت را ندارید.",
        };
      }
      return {
        status: "error",
        httpStatus,
        message: extractApiErrorMessage(err.response?.data, "خطا در ارتباط با سرور برای جست‌وجوی پالت."),
      };
    } finally {
      // فقط آخرین درخواست حالت «در حال اسکن» را پاک می‌کند
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setIsScanning(false);
      }
    }
  }, []);

  return { lookup, isScanning };
}
